import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Printer } from "lucide-react";
import { CHECKLIST } from "@/data/checklist";
import { signupUrl } from "@/data/networks";
import { Seo } from "@/components/hv/Seo";
import { FadeIn } from "@/components/hv/Motion";
import { LogoMark } from "@/components/hv/Logo";
import { networkFromPath, track } from "@/lib/track";

export default function ChecklistPage() {
  const print = () => {
    track("checklist_print", networkFromPath(document.referrer ? new URL(document.referrer).pathname : "/"));
    window.print();
  };

  return (
    <div data-testid="checklist-page" className="hv-print-root">
      <Seo
        title="The kitchen-table checklist — HearthVault"
        description="A printable checklist of the papers every family should be able to find: identity, money, insurance, home, digital life and the people to call."
      />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-24">
        <FadeIn className="flex flex-wrap items-center justify-between gap-4 print:hidden">
          <Link to="/facebook" data-testid="checklist-back-link" className="inline-flex items-center gap-1.5 text-sm text-[#685F58] hover:text-[#1F1A17]"><ArrowLeft className="h-4 w-4" /> Back</Link>
          <button type="button" onClick={print} data-testid="checklist-print-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-11 px-5 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
            <Printer className="h-4 w-4" /> Print or save as PDF
          </button>
        </FadeIn>

        <FadeIn delay={0.1} className="mt-10 sm:mt-14">
          <div className="flex items-center gap-3 text-[#B4532A]">
            <LogoMark className="h-6 w-6" />
            <span className="font-mono text-xs uppercase tracking-[0.25em]">HearthVault · printable</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1F1A17]" data-testid="checklist-title">{CHECKLIST.title}</h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-[#685F58]">{CHECKLIST.intro}</p>
        </FadeIn>

        <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-10 print:grid-cols-2">
          {CHECKLIST.groups.map((g, gi) => (
            <FadeIn key={g.t} delay={0.15 + gi * 0.05} className="break-inside-avoid">
              <h2 className="flex items-baseline gap-3 font-serif text-2xl tracking-tight text-[#1F1A17]" data-testid={`checklist-group-${gi}`}>
                <span className="font-mono text-xs text-[#B4532A]">0{gi + 1}</span>{g.t}
              </h2>
              <ul className="mt-4 space-y-3">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm leading-relaxed text-[#1F1A17]">
                    <span className="mt-0.5 h-4 w-4 shrink-0 rounded border border-[#1F1A17]/50" aria-hidden="true" />
                    {it}
                  </li>
                ))}
              </ul>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.5} className="mt-16 rounded-[2rem] border border-[#E8DDD3] bg-white p-8 sm:p-10 print:border-[#1F1A17]">
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight text-[#1F1A17]">{CHECKLIST.footer}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 print:hidden">
            <a href={signupUrl("checklist")} data-testid="checklist-signup-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#B4532A] hover:bg-[#9C421E] h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
              <LogoMark className="h-4 w-4" /> Create your vault — free <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <span className="text-sm text-[#685F58]">No card needed · Encrypted at rest</span>
          </div>
          <p className="hidden print:block mt-4 text-sm text-[#685F58]">hearthvault.family — the family document safe.</p>
        </FadeIn>
      </div>
    </div>
  );
}
